import { useState, useEffect } from 'react';
import { Button, Input, Spinner } from '../ui';
import { useSmtpPresets, type SmtpPreset, type SmtpSecurityMode, type TestSmtpResult } from '../../api/mailboxes';
import { diagnoseConnectionError } from '../../lib/error-messages';

function detectSmtpSettings(
  email: string,
  presets: Record<string, SmtpPreset> | undefined,
): SmtpPreset | null {
  const domain = email.split('@')[1]?.toLowerCase();
  if (!domain || !presets) return null;
  return presets[domain] ?? null;
}

export interface SmtpFormState {
  smtpHost: string;
  smtpPort: string;
  smtpSecurityMode: SmtpSecurityMode;
  smtpUser: string;
  smtpPassword: string;
  displayName: string;
}

interface SmtpSettingsProps {
  email: string;
  form: SmtpFormState;
  onChange: (field: keyof SmtpFormState, value: string | SmtpSecurityMode) => void;
  onTestConnection: () => Promise<TestSmtpResult>;
  disabled?: boolean;
  errors?: Partial<Record<keyof SmtpFormState, string>>;
}

export function SmtpSettings({
  email,
  form,
  onChange,
  onTestConnection,
  disabled = false,
  errors = {},
}: SmtpSettingsProps): JSX.Element {
  const [testResult, setTestResult] = useState<TestSmtpResult | null>(null);
  const [isTesting, setIsTesting] = useState(false);
  const [autoDetected, setAutoDetected] = useState(false);
  const { data: presets } = useSmtpPresets();

  // Auto-detect SMTP settings when email changes (only if host is empty)
  useEffect(() => {
    if (!form.smtpHost && email) {
      const preset = detectSmtpSettings(email, presets);
      if (preset) {
        onChange('smtpHost', preset.host);
        onChange('smtpPort', String(preset.port));
        onChange('smtpSecurityMode', preset.securityMode);
        onChange('smtpUser', email);
        setAutoDetected(true);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- intentionally run only when email or presets change
  }, [email, presets]);

  const handleTestConnection = async () => {
    setIsTesting(true);
    setTestResult(null);
    try {
      const result = await onTestConnection();
      setTestResult(result);
    } finally {
      setIsTesting(false);
    }
  };

  const securityModeOptions: { value: SmtpSecurityMode; label: string; description: string }[] = [
    { value: 'starttls', label: 'STARTTLS', description: 'Порт 587' },
    { value: 'smtps', label: 'SSL/TLS', description: 'Порт 465' },
    { value: 'plain', label: 'Без шифрования', description: 'Порт 25 (не рекомендуется)' },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h3 className="text-sm font-medium text-text-secondary uppercase tracking-wider">
          Настройки SMTP (для отправки)
        </h3>
        {autoDetected && (
          <span className="text-xs text-accent-brand">Автоопределено</span>
        )}
      </div>

      {/* Display Name */}
      <Input
        label="Имя отправителя"
        placeholder="Команда поддержки"
        value={form.displayName}
        onChange={(v) => onChange('displayName', v)}
        disabled={disabled}
        {...(errors.displayName && { error: errors.displayName })}
      />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="SMTP сервер"
          placeholder="smtp.example.com"
          value={form.smtpHost}
          onChange={(v) => onChange('smtpHost', v)}
          disabled={disabled}
          {...(errors.smtpHost && { error: errors.smtpHost })}
        />
        <Input
          label="Порт"
          placeholder="587"
          value={form.smtpPort}
          onChange={(v) => onChange('smtpPort', v)}
          disabled={disabled}
          {...(errors.smtpPort && { error: errors.smtpPort })}
        />
      </div>

      {/* Security Mode */}
      <div className="space-y-2">
        <label className="text-sm text-text-secondary">Шифрование</label>
        <div className="flex gap-2">
          {securityModeOptions.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => onChange('smtpSecurityMode', option.value)}
              disabled={disabled}
              className={`flex-1 px-3 py-2 rounded-md border text-sm transition-colors ${
                form.smtpSecurityMode === option.value
                  ? 'bg-accent-brand/10 border-accent-brand text-accent-brand'
                  : 'bg-surface-subtle border-border-subtle text-text-secondary hover:border-accent-brand/50'
              } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
            >
              <div className="font-medium">{option.label}</div>
              <div className="text-xs opacity-70">{option.description}</div>
            </button>
          ))}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <Input
          label="Пользователь"
          placeholder="mailbox@example.com"
          value={form.smtpUser}
          onChange={(v) => onChange('smtpUser', v)}
          disabled={disabled}
          {...(errors.smtpUser && { error: errors.smtpUser })}
        />
        <Input
          label="Пароль"
          type="password"
          placeholder="Пароль приложения или обычный"
          value={form.smtpPassword}
          onChange={(v) => onChange('smtpPassword', v)}
          disabled={disabled}
          {...(errors.smtpPassword && { error: errors.smtpPassword })}
        />
      </div>

      {/* Test Connection Button */}
      <div className="flex items-center gap-4">
        <Button
          type="button"
          variant="secondary"
          size="sm"
          onClick={handleTestConnection}
          disabled={disabled || isTesting || !form.smtpHost || !form.smtpUser || !form.smtpPassword}
        >
          {isTesting ? (
            <span className="flex items-center gap-2">
              <Spinner size="sm" />
              Проверка...
            </span>
          ) : (
            'Проверить подключение'
          )}
        </Button>

        {testResult && (
          <div className={`text-sm ${testResult.ok ? 'text-accent-success' : 'text-accent-danger'}`}>
            {testResult.ok ? (
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
                </svg>
                Подключение успешно ({testResult.server})
              </span>
            ) : (
              <span className="flex items-center gap-1">
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
                {(testResult.error && diagnoseConnectionError(testResult.error)) ?? testResult.error}
              </span>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export const initialSmtpState: SmtpFormState = {
  smtpHost: '',
  smtpPort: '587',
  smtpSecurityMode: 'starttls',
  smtpUser: '',
  smtpPassword: '',
  displayName: '',
};
